import type { PDFFont, PDFPage, RGB } from 'pdf-lib'

/**
 * 주문 한 건 → A4 PDF — 계정 페이지(My Order · Wholesale Orders)의 자세히 맨 아래 Download PDF (2026-10-06).
 *
 * 어떤 주문이든 `OrderDocumentModel`(찍을 글자만 모은 값)로 받아 같은 판에 짠다 — 페이지마다 자기 주문을
 * 이 모양으로 바꿔 넘긴다(retailOrder.ts · wholesale/wholesaleOrderDocument.ts).
 *
 * Works with 문서(deviceDocument.ts)와 같은 방식 — 화면을 그림으로 찍지 않고 값에서 다시 조판한다(pdf-lib).
 * 다른 점은 서체다. 국내 주문은 받는 곳 주소가 한글이고 금액에 ₩ 가 붙는데, PDF 표준 서체
 * (Helvetica, WinAnsi)는 둘 다 못 싣는다. 그래서 그런 글자가 하나라도 있으면 나눔고딕(public/fonts, 약 4MB)을
 * 그때만 받아 통째로 넣고(subset 은 글자가 빠졌다), 아니면 받을 것 없는 Helvetica 로 굽는다.
 */

/** 라벨 | 값 한 줄. 세 번째 값이 true 면 강조(합계) */
export type OrderDocumentRow = [string, string, boolean?]

export interface OrderDocumentModel {
	/** 문서 종류 — 오른쪽 위 도장과 이어지는 쪽의 머리글. 예 'ORDER' · 'WHOLESALE ORDER' */
	kind: string
	id: string
	/** 번호 아래 한 줄 — 접수일 · 상태 · 통화 */
	facts: string
	items: { name: string; unit: string; qty: string; total: string }[]
	/** Items 다음 구역들. alignRight = 값을 오른쪽 끝에(금액) */
	sections: { title: string; rows: OrderDocumentRow[]; alignRight: boolean }[]
}

// A4 세로 (pt) — deviceDocument 와 같은 판형
const PAGE_W = 595.28
const PAGE_H = 841.89
const MARGIN_X = 56
const MARGIN_TOP = 46
const MARGIN_BOTTOM = 60
const CONTENT_W = PAGE_W - MARGIN_X * 2

// 사이트 토큰과 같은 값 (monolith.tokens.ts)
const INK = [0.086, 0.094, 0.106] as const
const DIM = [0.373, 0.4, 0.439] as const
const FAINT = [0.604, 0.631, 0.667] as const
const LINE = [0.898, 0.906, 0.918] as const

const LOGO_LOCKUP = '/images/logo/logo_title.png'
const KOREAN_FONT = '/fonts/NanumGothic-Regular.ttf'

/** WinAnsi(CP1252) 밖의 글자 — 하나라도 있으면 한글 서체로 굽는다 */
const NON_WINANSI =
	/[^\u0020-\u007E\u00A0-\u00FF\u20AC\u201A\u0192\u201E\u2026\u2020\u2021\u02C6\u2030\u0160\u2039\u0152\u017D\u2018\u2019\u201C\u201D\u2022\u2013\u2014\u02DC\u2122\u0161\u203A\u0153\u017E\u0178]/

const LABEL_W = 130

const wrapText = (
	text: string,
	font: PDFFont,
	size: number,
	maxWidth: number,
): string[] => {
	const source = text.replace(/\s+/g, ' ').trim()
	if (source === '') return ['']
	const lines: string[] = []
	let line = ''
	Array.from(source.split(' ')).forEach(word => {
		const candidate = line === '' ? word : `${line} ${word}`
		if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
			line = candidate
			return
		}
		if (line !== '') lines.push(line)
		// 낱말 하나가 폭을 넘으면 글자 단위로 끊는다
		let chunk = ''
		Array.from(word).forEach(ch => {
			if (font.widthOfTextAtSize(chunk + ch, size) > maxWidth && chunk !== '') {
				lines.push(chunk)
				chunk = ''
			}
			chunk += ch
		})
		line = chunk
	})
	if (line !== '') lines.push(line)
	return lines
}

export const buildOrderDocument = async (
	text: OrderDocumentModel,
): Promise<Uint8Array> => {
	const { PDFDocument, StandardFonts, rgb } = await import('pdf-lib')

	const allStrings = [
		text.facts,
		...text.items.flatMap(i => [i.name, i.unit, i.total]),
		...text.sections.flatMap(aSection => aSection.rows.flatMap(([a, b]) => [a, b])),
	]
	const needsKorean = allStrings.some(s => NON_WINANSI.test(s))

	const load = async (path: string) => {
		const res = await fetch(path)
		if (!res.ok) throw new Error(`${path} ${res.status}`)
		return res.arrayBuffer()
	}

	const pdf = await PDFDocument.create()
	pdf.setTitle(`${text.kind} ${text.id}`)
	pdf.setAuthor('Judahs')
	pdf.setCreator('judahs.io')
	pdf.setProducer('judahs.io')

	let regular: PDFFont
	let bold: PDFFont
	if (needsKorean) {
		const { default: fontkit } = await import('@pdf-lib/fontkit')
		pdf.registerFontkit(fontkit)
		// subset 은 쓰지 않는다 — pdf-lib + fontkit 의 subset 이 이 서체에서 글리프를 잃어 글자가 거의 다 빠졌다
		// (2026-10-06 실측: ₩ 와 몇 글자만 남음). 서체를 통째로 넣어 문서가 3~4MB 가 된다.
		regular = await pdf.embedFont(await load(KOREAN_FONT))
		// 굵은 서체 파일을 하나 더 받지 않는다 — 강조는 크기로
		bold = regular
	} else {
		regular = await pdf.embedFont(StandardFonts.Helvetica)
		bold = await pdf.embedFont(StandardFonts.HelveticaBold)
	}
	const lockup = await pdf.embedPng(await load(LOGO_LOCKUP))

	const ink = rgb(...INK)
	const dim = rgb(...DIM)
	const faint = rgb(...FAINT)
	const line = rgb(...LINE)

	const pages: PDFPage[] = []
	let page!: PDFPage
	let y = 0

	const drawTracked = (
		value: string,
		x: number,
		at: number,
		size: number,
		color: RGB,
	) => {
		let cx = x
		Array.from(value).forEach(ch => {
			page.drawText(ch, { x: cx, y: at, size, font: regular, color })
			cx += regular.widthOfTextAtSize(ch, size) + 1.1
		})
	}
	const trackedWidth = (value: string, size: number) =>
		Array.from(value).reduce(
			(w, ch) => w + regular.widthOfTextAtSize(ch, size) + 1.1,
			0,
		) - 1.1

	const rule = (at: number) =>
		page.drawLine({
			start: { x: MARGIN_X, y: at },
			end: { x: PAGE_W - MARGIN_X, y: at },
			thickness: 0.5,
			color: line,
		})

	const newPage = () => {
		page = pdf.addPage([PAGE_W, PAGE_H])
		pages.push(page)
		y = PAGE_H - MARGIN_TOP
		if (pages.length > 1) {
			drawTracked(`${text.kind} ${text.id}`, MARGIN_X, y - 8, 7.5, faint)
			rule(y - 16)
			y -= 34
		}
	}
	const ensure = (needed: number) => {
		if (y - needed < MARGIN_BOTTOM) newPage()
	}

	newPage()

	// ── 표제부 ──
	const lockupW = 104
	const lockupH = (lockup.height / lockup.width) * lockupW
	page.drawImage(lockup, { x: MARGIN_X, y: y - lockupH, width: lockupW, height: lockupH })
	const stamp = text.kind
	drawTracked(
		stamp,
		PAGE_W - MARGIN_X - trackedWidth(stamp, 7.5),
		y - lockupH + 2,
		7.5,
		faint,
	)
	y -= lockupH + 20
	rule(y)
	y -= 24

	drawTracked('ORDER NO.', MARGIN_X, y, 8, faint)
	y -= 26
	page.drawText(text.id, { x: MARGIN_X, y, size: 22, font: regular, color: ink })
	y -= 18
	page.drawText(text.facts, { x: MARGIN_X, y, size: 9, font: regular, color: dim })
	y -= 16
	rule(y)
	y -= 30

	// 구역 = 제목 + 행들. 행 수만큼 미리 확보해 쪽 경계에서 쪼개지지 않게 한다(한 줄만 다음 쪽으로 넘어가던 것)
	const sectionTitle = (title: string, rowCount = 1) => {
		ensure(40 + rowCount * 26)
		drawTracked(title.toUpperCase(), MARGIN_X, y, 8, faint)
		y -= 12
		rule(y)
		y -= 15
	}

	// ── 품목 ──
	const COL = { name: CONTENT_W - 110 - 50 - 110, unit: 110, qty: 50, total: 110 }
	sectionTitle('Items', text.items.length + 1)
	const rightText = (
		value: string,
		rightEdge: number,
		at: number,
		size: number,
		font: PDFFont,
		color: RGB,
	) =>
		page.drawText(value, {
			x: rightEdge - font.widthOfTextAtSize(value, size),
			y: at,
			size,
			font,
			color,
		})
	// 열 머리
	drawTracked('PRODUCT', MARGIN_X, y, 7, faint)
	const unitRight = MARGIN_X + COL.name + COL.unit
	const qtyRight = unitRight + COL.qty
	const totalRight = PAGE_W - MARGIN_X
	drawTracked('UNIT PRICE', unitRight - trackedWidth('UNIT PRICE', 7), y, 7, faint)
	drawTracked('QTY', qtyRight - trackedWidth('QTY', 7), y, 7, faint)
	drawTracked('LINE TOTAL', totalRight - trackedWidth('LINE TOTAL', 7), y, 7, faint)
	y -= 10
	rule(y)
	y -= 16
	text.items.forEach(anItem => {
		const nameLines = wrapText(anItem.name, regular, 10, COL.name - 12)
		const rowH = nameLines.length * 13 + 4
		ensure(rowH + 4)
		nameLines.forEach((aLine, i) =>
			page.drawText(aLine, {
				x: MARGIN_X,
				y: y - i * 13,
				size: 10,
				font: regular,
				color: ink,
			}),
		)
		rightText(anItem.unit, unitRight, y, 10, regular, dim)
		rightText(anItem.qty, qtyRight, y, 10, regular, dim)
		rightText(anItem.total, totalRight, y, 10, regular, ink)
		y -= rowH
		rule(y + 3)
		y -= 11
	})
	y -= 10

	// 라벨 | 값 행 (결제 · 받는 사람 · 배송)
	const labelRows = (rows: [string, string, boolean?][], alignRight: boolean) => {
		rows.forEach(([label, value, strong]) => {
			const size = strong ? 11 : 10
			const font = strong ? bold : regular
			const valueLines = alignRight
				? [value]
				: wrapText(value, font, size, CONTENT_W - LABEL_W - 8)
			const rowH = valueLines.length * 14 + 4
			ensure(rowH + 4)
			drawTracked(label.toUpperCase(), MARGIN_X, y + 1, 7, faint)
			valueLines.forEach((aLine, i) => {
				if (alignRight) {
					rightText(aLine, totalRight, y - i * 14, size, font, ink)
				} else {
					page.drawText(aLine, {
						x: MARGIN_X + LABEL_W,
						y: y - i * 14,
						size,
						font,
						color: ink,
					})
				}
			})
			y -= rowH
			rule(y + 3)
			y -= 11
		})
		y -= 10
	}

	text.sections.forEach(aSection => {
		sectionTitle(aSection.title, aSection.rows.length)
		labelRows(aSection.rows, aSection.alignRight)
	})

	// ── 쪽마다 바닥글 ──
	pages.forEach((aPage, index) => {
		page = aPage
		const left = `judahs.io   ·   Generated ${new Date().toISOString().slice(0, 10)}`
		page.drawText(left, { x: MARGIN_X, y: 30, size: 7.5, font: regular, color: faint })
		const right = `${index + 1} / ${pages.length}`
		page.drawText(right, {
			x: PAGE_W - MARGIN_X - regular.widthOfTextAtSize(right, 7.5),
			y: 30,
			size: 7.5,
			font: regular,
			color: faint,
		})
	})

	return pdf.save()
}

export const downloadOrderDocument = async (
	model: OrderDocumentModel,
	fileName: string,
): Promise<void> => {
	const bytes = await buildOrderDocument(model)
	const blob = new Blob([bytes], { type: 'application/pdf' })
	const url = URL.createObjectURL(blob)
	const a = document.createElement('a')
	a.href = url
	a.download = fileName
	document.body.appendChild(a)
	a.click()
	a.remove()
	window.setTimeout(() => URL.revokeObjectURL(url), 1000)
}
